'use client';

import React, { useState } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { EssayScoreResult } from '@/app/score/types/score';
import { ReportEditorProps, StructuredWritingData } from '@/types/writing';
import { Task1Type, VisualType } from '@/types/task-bank';
import { ReportEditorContent } from './report-editor-content';
import { ReportEditorSidebar } from './report-editor-sidebar';
import { ReportEditorModals } from './report-editor-modals';
import { useReportEditorState } from '@/hooks/use-report-editor-state';
import { useAuth } from '@/context/auth-context';
import { User } from 'firebase/auth';

function isFirebaseUser(u: unknown): u is User {
  return !!u && typeof u === 'object' && 'uid' in u && 'getIdToken' in u;
}

export function ReportEditor({
  initialPrompt = '',
  initialIntroduction = '',
  initialOverview = '',
  initialDetailParagraphs = [
    { type: '', content: '' },
    { type: '', content: '' },
  ],
  initialImageUrl = '',
  initialTask1Type,
  initialVisualType,
  initialReportPlan,
  initialEssayId = null,
  onSuggestionSuccess,
  suggestionClearTrigger,
}: ReportEditorProps) {
  const { user, userProfile } = useAuth();

  const state = useReportEditorState({
    initialPrompt,
    initialIntroduction,
    initialOverview,
    initialDetailParagraphs,
    initialReportPlan,
    initialEssayId,
    onSuggestionSuccess,
    suggestionClearTrigger,
    user: isFirebaseUser(user) ? user : null,
    userProfile,
  });

  // Task 1 specific state (keeping existing functionality)
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [task1Type, setTask1Type] = useState(initialTask1Type);
  const [visualType, setVisualType] = useState(initialVisualType);

  const structuredReportData: StructuredWritingData = {
    type: 'report',
    introduction: state.introduction,
    bodyParagraphs: state.bodyParagraphs,
    overview: state.overview, // Now properly using overview instead of conclusion
  };

  const [isScoreSheetOpen, setIsScoreSheetOpen] = useState(false);
  const scoreResult = state.score as EssayScoreResult | null;

  const handleSelectTask = (task: {
    imageUrl?: string;
    task1Type?: Task1Type;
    visualType?: VisualType;
  }) => {
    if (task.imageUrl) setImageUrl(task.imageUrl);
    if (task.task1Type) setTask1Type(task.task1Type);
    if (task.visualType) setVisualType(task.visualType);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'flex flex-col md:flex-row gap-4 flex-grow',
          'min-h-[calc(100vh-200px)]'
        )}
      >
        {/* Left column - Report Editor */}
        <div className='w-full md:w-1/2 flex flex-col space-y-4'>
          <ReportEditorContent
            hasSelectedTask={!!state.taskBank.selectedTask}
            introduction={state.introduction}
            overview={state.overview} // Now properly using overview
            bodyParagraphs={state.bodyParagraphs}
            onIntroductionChange={state.handleIntroductionChange}
            onOverviewChange={state.handleOverviewChange} // Proper overview handler
            onBodyParagraphChange={state.handleBodyParagraphChange}
            onAddBodyParagraph={state.addBodyParagraph}
            onRemoveBodyParagraph={state.removeBodyParagraph}
            isSlashMenuOpen={state.isSlashMenuOpen}
            handleSlashTrigger={state.handleSlashTrigger}
            handleSlashFilterUpdate={state.handleSlashFilterUpdate}
            handleSlashClose={state.handleSlashClose}
            handleTextareaKeyDown={state.handleSlashCommandTextareaKeyDown}
            saveStatus={state.saveStatus}
            totalWordCount={state.totalWordCount}
            currentContent={state.currentContent}
            initialPrompt={initialPrompt}
          />
        </div>

        {/* Right column - Task info, visual, plan, and controls */}
        <div className='w-full md:w-1/2 flex flex-col space-y-4'>
          <ReportEditorSidebar
            hasSelectedTask={!!state.taskBank.selectedTask}
            selectedTask={state.taskBank.selectedTask}
            user={isFirebaseUser(user) ? user : null}
            prompt={state.prompt}
            imageUrl={imageUrl}
            showIdeas={state.taskBank.showIdeas}
            setShowIdeas={state.taskBank.setShowIdeas}
            setTaskBankOpen={state.taskBank.setTaskBankOpen}
            isProUser={state.isProUser}
            isScoring={state.isScoring}
            essayData={structuredReportData}
            currentEssayId={state.currentEssayId}
            fetchNewScore={(prompt, data) =>
              state.fetchNewScore(
                prompt,
                data,
                'task1',
                undefined,
                undefined,
                task1Type,
                visualType
              )
            }
            canScore={state.scoringRequirementsList.length === 0}
            requirements={state.scoringRequirementsList}
            plan={state.plan}
            isPlanOpen={state.isPlanOpen}
            onPlanOpenChange={state.setIsPlanOpen}
            handlePlanContentChange={state.handlePlanContentChange}
            addPlanItem={state.addPlanItem}
            removePlanItem={state.removePlanItem}
          />
        </div>
      </div>

      <ReportEditorModals
        isProUser={state.isProUser}
        scoringRequirementsList={state.scoringRequirementsList}
        isScoring={state.isScoring}
        structuredReportData={structuredReportData}
        prompt={state.prompt}
        task1Type={task1Type}
        visualType={visualType}
        fetchNewScore={state.fetchNewScore}
        setIsScoreSheetOpen={setIsScoreSheetOpen}
        isSlashMenuOpen={state.isSlashMenuOpen}
        slashMenuTargetElement={state.slashMenuTargetElement}
        slashMenuFilteredCommands={state.slashMenuFilteredCommands}
        slashMenuSelectedIndex={state.slashMenuSelectedIndex}
        executeSlashCommand={state.executeSlashCommand}
        handleSlashClose={state.handleSlashClose}
        setSlashMenuSelectedIndex={state.setSlashMenuSelectedIndex}
        isCommandResultPopoverOpen={state.isCommandResultPopoverOpen}
        commandResultTargetElement={state.commandResultTargetElement}
        commandResult={state.commandResult}
        commandError={state.commandError}
        isCommandLoading={state.isCommandLoading}
        closeCommandResultPopover={state.closeCommandResultPopover}
        activeCommandId={state.activeCommandId}
        taskBankOpen={state.taskBank.taskBankOpen}
        setTaskBankOpen={state.taskBank.setTaskBankOpen}
        handleSelectTask={handleSelectTask}
        selectedTaskId={state.taskBank.selectedTask?.id || null}
        showMinimalChangeDialog={state.showMinimalChangeDialog}
        setShowMinimalChangeDialog={state.setShowMinimalChangeDialog}
        scoreResult={scoreResult}
        score={state.score}
        isScoreSheetOpen={isScoreSheetOpen}
        scoreError={state.scoreError}
      />
    </TooltipProvider>
  );
}
